import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import useUpload from 'hooks/useUpload'
import useMutation from 'hooks/useMutation'

function Table(props) {
  let { t } = useTranslation();
  let [config, setConfig] = useState({
    firstLineHeader: /nombre|apellido/gi.test(props.table.csv?.data?.at(0)?.join(' ')),
    identificationDuplicate: 'ignore'
  })
  let upload = useUpload()
  let [status, setStatus] = useState('ready')

  let [mutate, mutation] = useMutation(window._l(`/consumers/migrate`).substr(1), {
    onMutate: (data) => {

    },
    onSuccess: (data) => {
      // Bug real (auditoría 2026-08-16): props.ready() (saca la fila de la
      // lista de pendientes y agenda un refetch, ver Migrate.js) se llamaba
      // en load() apenas terminaba upload() — ANTES de que mutate(payload)
      // confirmara que el backend aceptó el job de migración. Si mutate
      // fallaba después, la fila ya había desaparecido de "pendientes"
      // (onError sólo muestra un toast transitorio) y el operador se
      // quedaba sin ninguna señal persistente de que ese CSV seguía sin
      // importarse. Ahora sólo se saca de pendientes cuando el job
      // realmente fue aceptado.
      props.ready(props.table.key);
    },
    onError: (error) => {
      window.notify({
        message: error.message,
        type: "danger",
        title: "Error",
      });
    },
  });

  let remove = () => {
    props.remove(props.table.key)
  }

  let load = async () => {
    // Bug real (auditoría 2026-08-16): alert() solo tiene botón OK y SIEMPRE
    // continúa la ejecución al cerrarse — no cancela nada. El mensaje avisa
    // de una operación que puede demorar hasta 24hs y puede crear/duplicar
    // pacientes en todo el equipo, pero el operador no tenía forma real de
    // dar marcha atrás. window.confirm() sí permite cancelar.
    if (!window.confirm(t('consumers_csv_process_message', 'Se va a iniciar el proceso para importar pacientes. Esto puede demorar de unos pocos minutos hasta 24hs'))) return
    let { table } = props
    let file = await upload(table.file).then(links => links.at(0)).catch(() => null);
    if (!file) return
    let payload = {
      file,
      key: table.key,
      name: table.name,
      firstLineHeader: config.firstLineHeader,
      identificationDuplicate: config.identificationDuplicate,
    }
    setStatus('ready')
    mutate(payload);
  }

  return (
    <div>
      <div className="d-flex gap-3 align-items-center px-1 mb-2">
        <div className="d-flex align-items-center gap-3">
          <div className="dropdown bg-white">
            <button
              className="btn btn-sm btn-dark"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
            >
              <div>{t('actions', 'Acciones')}</div>
            </button>
            {/* Bug real (auditoría 2026-08-17, responsive): minWidth: 300 fijo
                es ~80% del ancho de un viewport de 375px — dejaba un margen
                mínimo y, combinado con el bug del <pre> de abajo empujando el
                layout de la fila, agravaba el riesgo de overflow horizontal.
                Ahora el ancho mínimo cede ante el viewport disponible. */}
            <div className="dropdown-menu shadow-lg border-0 p-0" style={{ minWidth: 'min(300px, calc(100vw - 32px))' }}>
              <div className="p-3 d-flex align-items-center border-bottom">
                <input
                  className="form-check-input me-3"
                  type="checkbox"
                  checked={!!config.firstLineHeader}
                  id={`ignoreFirstLine${props.table.key}`}
                  onChange={() => setConfig({ ...config, firstLineHeader: !config.firstLineHeader })}
                />
                <label className="form-check-label" htmlFor={`ignoreFirstLine${props.table.key}`}>
                  {t('ignore_first_line', 'Ignorar la primera linea')}
                </label>
              </div>
              <div className="border-bottom p-3">
                <small className="text-secondary">Si ya exsite el DNI</small>
              </div>
              <div className="p-3 d-flex align-items-center border-bottom">
                <input
                  className="form-check-input me-3"
                  type="radio"
                  checked={config.identificationDuplicate === 'ignore'}
                  id={`identificationDuplicateIgnore${props.table.key}`}
                  onChange={() => setConfig({ ...config, identificationDuplicate: 'ignore' })}
                />
                <label className="form-check-label" htmlFor={`identificationDuplicateIgnore${props.table.key}`}>
                  {t('consumers_csv_identification_ignore', 'Ignorar')}
                </label>
              </div>
              <div className="p-3 d-flex align-items-center border-bottom">
                <input
                  className="form-check-input me-3"
                  type="radio"
                  checked={config.identificationDuplicate === 'create'}
                  id={`identificationDuplicateCreate${props.table.key}`}
                  onChange={() => setConfig({ ...config, identificationDuplicate: 'create' })}
                />
                <label className="form-check-label" htmlFor={`identificationDuplicateCreate${props.table.key}`}>
                  {t('consumers_csv_identification_create', 'Duplicar')}
                </label>
              </div>
              <div className="p-3 d-flex align-items-center border-bottom">
                <input
                  className="form-check-input me-3"
                  type="radio"
                  checked={config.identificationDuplicate === 'replace'}
                  id={`identificationDuplicateReplace${props.table.key}`}
                  onChange={() => setConfig({ ...config, identificationDuplicate: 'replace' })}
                />
                <label className="form-check-label" htmlFor={`identificationDuplicateReplace${props.table.key}`}>
                  {t('consumers_csv_identification_replace', 'Reemplazar')}
                </label>
              </div>
              <div className="d-flex align-items-center">
                {props.remove && (
                  <div className="border-end flex-fill">
                    <button className="btn py-3 border-0 w-100" onClick={remove}>
                      {t('consumers_csv_remove', 'Descartar')}
                    </button>
                  </div>
                )}
                <div className="flex-fill">
                  {/* Bug real (auditoría 2026-08-16): el botón no tenía
                      disabled — sólo un spinner decorativo atado a
                      mutation.isLoading. Un doble click durante el
                      `await upload(...)` disparaba dos uploads + dos
                      mutate() concurrentes para el mismo CSV, creando jobs
                      de importación duplicados. */}
                  <button className="btn py-3 border-0  w-100" onClick={load} disabled={mutation.isLoading}>
                    {mutation.isLoading && (
                      <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                    )} {t('consumers_csv_import', 'Procesar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Bug real (auditoría 2026-08-17, responsive): <pre> hereda
            white-space: pre del navegador — ninguna clase de Bootstrap
            (m-0/text-secondary) lo pisa — así que un nombre de archivo largo
            no tenía ningún punto de wrap. Este div tampoco tenía
            min-width:0 (los flex items arrancan en min-width:auto, que
            impide encogerse por debajo del contenido), así que a 375px el
            <pre> empujaba esta fila y, al no estar dentro de un contenedor
            con scroll propio, la página entera a scroll horizontal. */}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="text-secondary text-truncate">{props.table.name} {Array.isArray(props.table.csv?.data) && (
            <sup className="text-secondary">({props.table.csv.data.length - !!config.firstLineHeader} {t('consumers_csv_records_length', 'registros')})</sup>
          )}</div>
        </div>
      </div>
      {Array.isArray(props.table.csv?.data) && (
        <div className="shadow-sm rounded">
          <div className="table-responsive">
            <table className="table bg-white table-bordered mb-0">
              <thead>
                <tr>
                  <th className="text-nowrap text-first-up">{t('first_name', 'nombre')}</th>
                  <th className="text-nowrap text-first-up">{t('last_name', 'apellido')}</th>
                  <th className="text-nowrap text-first-up">{t('identification', 'DNI', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('dob', 'fecha nacimiento', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('gender', 'genero', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('email', 'email')}</th>
                  <th className="text-nowrap text-first-up">{t('phone', 'telefono')}</th>
                  <th className="text-nowrap text-first-up">{t('financier_name', 'cobertura', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('financier_plan', 'plan', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('financier_identification', 'nro. afiliado', { context: 'consumer_migrate' })}</th>
                  <th className="text-nowrap text-first-up">{t('occupation', 'Ocupación', { context: "consumers" })}</th>
                  <th className="text-nowrap text-first-up">{t('notes')}</th>
                  <th className="text-nowrap text-first-up">{t('country', 'pais')}</th>
                  <th className="text-nowrap text-first-up">{t('address', 'direccion')}</th>
                  <th className="text-nowrap text-first-up">{t('reference')}</th>
                </tr>
              </thead>
              <tbody>
                {props.table.csv.data.map((row, index1) => config.firstLineHeader && index1 === 0 ? (
                  null
                ) :(
                  <tr key={index1}>
                    {row.map((cell, index2) => (
                      <td className="text-nowrap" key={index2}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

          </div>
        </div>
      )}
    </div>
  );
}

export default Table;
